import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Icons, iconSize, palette, Stack, Typography } from '@munifocus/ui';

const WIDTH = 480;
const HEIGHT = 160;

/**
 * A drawn signature. Pointer events, so a mouse, a pen and a finger all work;
 * the canvas is exported as a transparent PNG data URL whenever a stroke ends.
 * `onChange(null)` while the pad is empty, so the caller cannot sign blank.
 */
export function SignaturePad({ onChange }: { onChange: (dataUrl: string | null) => void }) {
  const { t } = useTranslation();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  // A ref as well as state: a stroke can end before the re-render that the
  // first segment's state update asks for.
  const inked = useRef(false);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const ctx = canvas.current?.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = palette.text.primary;
  }, []);

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / (rect.width || WIDTH)) * WIDTH,
      y: ((e.clientY - rect.top) / (rect.height || HEIGHT)) * HEIGHT,
    };
  };

  const start = (e: PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvas.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const { x, y } = point(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = canvas.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = point(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (!inked.current) {
      inked.current = true;
      setEmpty(false);
    }
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (inked.current) onChange(canvas.current?.toDataURL('image/png') ?? null);
  };

  const clear = () => {
    const ctx = canvas.current?.getContext('2d');
    ctx?.clearRect(0, 0, WIDTH, HEIGHT);
    inked.current = false;
    setEmpty(true);
    onChange(null);
  };

  return (
    <Stack spacing={0.75}>
      <Box
        sx={{
          border: `1px solid ${palette.border.lineStrong}`,
          borderRadius: 1,
          bgcolor: palette.background.paper,
          touchAction: 'none',
          maxWidth: WIDTH,
        }}
      >
        <canvas
          ref={canvas}
          width={WIDTH}
          height={HEIGHT}
          aria-label={t('gov.signoff.sign.padLabel')}
          role="img"
          style={{ width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
      </Box>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {t('gov.signoff.sign.padHint')}
        </Typography>
        <Button
          size="small"
          variant="text"
          startIcon={<Icons.Eraser size={iconSize.sm} />}
          onClick={clear}
          disabled={empty}
        >
          {t('gov.signoff.sign.clear')}
        </Button>
      </Stack>
    </Stack>
  );
}
